import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { FileText, Globe, Plus, Search, LogOut, LayoutGrid, Lock, ExternalLink, RefreshCw, Activity, X } from "lucide-react";
import pillars from "@/lib/pillars.json";
import { PostEditorCM, SeoEditorCM, auditPost, auditSeo, fmtDate } from "@/pages/admin/contentEditors";

const COUNTRIES = ["Spain", "Portugal", "Italy", "All"];

function HealthBadge({ audit }) {
  if (audit.errors.length) return <span data-testid="cm-health-error" className="px-2 py-0.5 text-[10px] tracking-wider uppercase bg-[#7a1d1d]/15 text-[#7a1d1d]">{audit.errors.length} error{audit.errors.length > 1 ? "s" : ""}</span>;
  if (audit.warnings.length) return <span data-testid="cm-health-warn" className="px-2 py-0.5 text-[10px] tracking-wider uppercase bg-[#8a6d1b]/15 text-[#8a6d1b]">{audit.warnings.length} warn</span>;
  return <span data-testid="cm-health-ok" className="px-2 py-0.5 text-[10px] tracking-wider uppercase bg-[#0D231B]/15 text-[#0D231B]">OK</span>;
}
function StatusPill({ v }) {
  const map = { verified: "bg-[#0D231B]/15 text-[#0D231B]", review: "bg-[#8a6d1b]/15 text-[#8a6d1b]", review_required: "bg-[#7a1d1d]/15 text-[#7a1d1d]" };
  return <span className={`px-2 py-0.5 text-[10px] tracking-wider uppercase ${map[v] || "bg-[#451C25]/10 text-[#6B4954]"}`}>{v || "—"}</span>;
}

export default function ContentManager() {
  const { user, loading, logout, authedAxios } = useAdminAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [seoPages, setSeoPages] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null); // { type:'post'|'seo', raw?, isNew? }
  const [showPillars, setShowPillars] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [activity, setActivity] = useState([]);
  const [activityBusy, setActivityBusy] = useState(false);

  const loadActivity = () => {
    setActivityBusy(true);
    authedAxios.get("/admin/connector-activity?limit=200")
      .then((r) => setActivity(r.data || []))
      .catch(() => toast.error("Could not load connector activity."))
      .finally(() => setActivityBusy(false));
  };
  const openActivity = () => { setShowActivity(true); loadActivity(); };

  const [fType, setFType] = useState("all");
  const [fPub, setFPub] = useState("all");
  const [fEdit, setFEdit] = useState("all");
  const [fCountry, setFCountry] = useState("all");
  const [fRoute, setFRoute] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => { if (!loading && !user) navigate("/admin", { replace: true }); }, [loading, user, navigate]);

  const load = () => {
    setBusy(true);
    Promise.all([authedAxios.get("/admin/posts"), authedAxios.get("/admin/seo-pages")])
      .then(([p, s]) => { setPosts(p.data || []); setSeoPages(s.data || []); })
      .catch(() => toast.error("Could not load content."))
      .finally(() => setBusy(false));
  };
  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const items = useMemo(() => {
    const a = posts.map((p) => ({ key: `post:${p.id}`, type: "post", title: p.title, slug: p.slug, url: `/blog/${p.slug}`, published: p.published, editorial_status: p.editorial_status, country: p.country_tag, route: (p.route_tags || []).join(", "), updated_at: p.updated_at, last_reviewed: p.last_reviewed, raw: p, audit: auditPost(p) }));
    const b = seoPages.map((s) => ({ key: `seo:${s.slug}`, type: "seo", title: s.h1, slug: s.slug, url: `/${s.slug}`, published: s.published, editorial_status: s.editorial_status, country: s.country, route: s.intent, updated_at: s.updated_at, last_reviewed: s.last_reviewed, raw: s, audit: auditSeo(s) }));
    return [...a, ...b];
  }, [posts, seoPages]);

  const filtered = useMemo(() => items.filter((it) => {
    if (fType !== "all" && it.type !== fType) return false;
    if (fPub === "published" && !it.published) return false;
    if (fPub === "draft" && it.published) return false;
    if (fEdit !== "all" && (it.editorial_status || "") !== fEdit) return false;
    if (fCountry !== "all" && it.country !== fCountry) return false;
    if (fRoute !== "all" && !String(it.route || "").split(", ").includes(fRoute)) return false;
    if (q.trim()) { const s = q.toLowerCase(); if (!(it.title || "").toLowerCase().includes(s) && !(it.slug || "").toLowerCase().includes(s)) return false; }
    return true;
  }).sort((x, y) => String(y.updated_at || "").localeCompare(String(x.updated_at || ""))), [items, fType, fPub, fEdit, fCountry, fRoute, q]);

  const pillarRoutes = useMemo(() => {
    const sp = pillars.spain || {};
    return Object.entries(sp).filter(([, v]) => v && typeof v === "object" && (v.h1 || v.hero_h1)).map(([slug, v]) => ({ slug, h1: v.h1 || v.hero_h1 }));
  }, []);

  if (loading) return <div className="min-h-screen bg-[#F5EFE7] flex items-center justify-center text-[#6B4954]">Loading…</div>;
  if (!user) return null;

  const counts = { all: items.length, post: items.filter((i) => i.type === "post").length, seo: items.filter((i) => i.type === "seo").length };

  return (
    <div data-testid="content-manager" className="min-h-screen bg-[#F5EFE7] text-[#451C25]">
      <header className="border-b border-[#451C25]/10 bg-[#F5EFE7] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <Link to="/" className="inline-flex items-center gap-3"><BrandLogo /></Link>
            <span className="hidden md:inline text-[11px] tracking-[0.22em] uppercase text-[#6B4954]">Content Manager</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" data-testid="cm-to-dashboard" className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase text-[#6B4954] hover:text-[#451C25]"><LayoutGrid size={14} /> Dashboard</Link>
            <button data-testid="cm-logout" onClick={() => { logout(); navigate("/admin"); }} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase text-[#6B4954] hover:text-[#451C25]"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight">Content</h1>
            <p className="text-[#6B4954] text-sm mt-1">{counts.all} items · {counts.post} articles · {counts.seo} SEO pages</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="cm-refresh" onClick={load} className="inline-flex items-center gap-2 border border-[#451C25]/30 px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase hover:bg-[#451C25] hover:text-white transition-colors"><RefreshCw size={14} /> Refresh</button>
            <button data-testid="cm-connector-activity" onClick={openActivity} className="inline-flex items-center gap-2 border border-[#451C25]/30 px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase hover:bg-[#451C25] hover:text-white transition-colors"><Activity size={14} /> Connector log</button>
            <button data-testid="cm-new-article" onClick={() => setEditing({ type: "post", isNew: true })} className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase hover:bg-[#2B1017] transition-colors"><Plus size={14} /> New article</button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <Seg label="Type" value={fType} on={setFType} options={[["all", "All"], ["post", "Articles"], ["seo", "SEO Pages"]]} testid="cm-filter-type" />
          <Seg label="State" value={fPub} on={setFPub} options={[["all", "All"], ["published", "Published"], ["draft", "Draft"]]} testid="cm-filter-pub" />
          <Seg label="Editorial" value={fEdit} on={setFEdit} options={[["all", "All"], ["verified", "Verified"], ["review", "Review"], ["review_required", "Re-check"]]} testid="cm-filter-edit" />
          <DD label="Country" value={fCountry} on={setFCountry} options={["all", ...COUNTRIES]} testid="cm-filter-country" />
          <DD label="Route" value={fRoute} on={setFRoute} options={["all", "general", "work", "retire", "home", "relocate"]} testid="cm-filter-route" />
          <div className="flex-1 min-w-[200px] flex items-center gap-2 border-b border-[#451C25]/20 focus-within:border-[#451C25]">
            <Search size={14} className="text-[#6B4954]" />
            <input data-testid="cm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or slug…" className="flex-1 bg-transparent py-2 text-sm focus:outline-none" />
          </div>
        </div>

        {/* List */}
        {busy ? (
          <div className="h-56 bg-white border border-[#451C25]/10 animate-pulse" />
        ) : filtered.length === 0 ? (
          <p className="text-[#6B4954] py-16 text-center bg-white border border-[#451C25]/10">No content matches these filters.</p>
        ) : (
          <div className="border border-[#451C25]/10 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#EDE4D6]/60">
                <tr className="text-left">
                  {["Title", "Type", "State", "Editorial", "Country", "Route/Intent", "Updated", "Reviewed", "SEO", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6B4954] font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#451C25]/10">
                {filtered.map((it) => (
                  <tr key={it.key} data-testid={`cm-row-${it.slug}`} className="hover:bg-[#F5EFE7]">
                    <td className="px-4 py-3 max-w-[280px]">
                      <div className="font-medium truncate">{it.title}</div>
                      <div className="text-[11px] text-[#6B4954] truncate">{it.url}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-[11px] text-[#6B4954]">{it.type === "post" ? <FileText size={13} /> : <Globe size={13} />}{it.type === "post" ? "Article" : "SEO"}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{it.published ? <span className="text-[11px] text-[#0D231B]">Published</span> : <span className="text-[11px] text-[#8a6d1b]">Draft</span>}</td>
                    <td className="px-4 py-3"><StatusPill v={it.editorial_status} /></td>
                    <td className="px-4 py-3 text-[12px] text-[#6B4954] whitespace-nowrap">{it.country || "—"}</td>
                    <td className="px-4 py-3 text-[12px] text-[#6B4954] whitespace-nowrap">{it.route || "—"}</td>
                    <td className="px-4 py-3 text-[12px] text-[#6B4954] whitespace-nowrap">{fmtDate(it.updated_at)}</td>
                    <td className="px-4 py-3 text-[12px] text-[#6B4954] whitespace-nowrap">{it.last_reviewed || "—"}</td>
                    <td className="px-4 py-3"><HealthBadge audit={it.audit} /></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <a href={it.url} target="_blank" rel="noreferrer" title="Open public page" className="inline-flex text-[#6B4954] hover:text-[#451C25] mr-3"><ExternalLink size={14} /></a>
                      <button data-testid={`cm-edit-${it.slug}`} onClick={() => setEditing({ type: it.type, raw: it.raw })} className="text-[11px] tracking-[0.18em] uppercase text-[#451C25] hover:text-[#2B1017]">Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Read-only pillar reference */}
        <div className="mt-10 border border-[#451C25]/10 bg-white">
          <button data-testid="cm-pillars-toggle" onClick={() => setShowPillars((s) => !s)} className="w-full flex items-center justify-between px-5 py-4 text-left">
            <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#6B4954]"><Lock size={13} /> Pillars (architecture reference · read-only)</span>
            <span className="text-[11px] text-[#6B4954]">{showPillars ? "Hide" : "Show"}</span>
          </button>
          {showPillars && (
            <div className="px-5 pb-5 space-y-2" data-testid="cm-pillars-list">
              <p className="text-[12px] text-[#6B4954]">The four Spain pillars live in <code>pillars.json</code> and are editable only via deployment. They are shown here for reference and cannot be edited in the Content Manager.</p>
              {pillarRoutes.map((pr) => (
                <div key={pr.slug} className="flex items-center justify-between bg-[#F5EFE7] border border-[#451C25]/10 px-4 py-2.5">
                  <div className="min-w-0"><div className="text-[13px] truncate">{pr.h1}</div><div className="text-[11px] text-[#6B4954]">/spain/{pr.slug}/</div></div>
                  <a href={`/spain/${pr.slug}/`} target="_blank" rel="noreferrer" className="text-[#6B4954] hover:text-[#451C25]"><ExternalLink size={14} /></a>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {editing?.type === "post" && (
        <PostEditorCM axios={authedAxios} initial={editing.isNew ? null : editing.raw} onClose={() => { setEditing(null); load(); }} onSaved={() => load()} />
      )}
      {editing?.type === "seo" && (
        <SeoEditorCM axios={authedAxios} initial={editing.raw} onClose={() => { setEditing(null); load(); }} onSaved={() => load()} />
      )}

      {showActivity && (
        <div data-testid="cm-activity-modal" className="fixed inset-0 z-50 bg-[#2B1017]/60 flex items-start justify-center p-4 md:p-8 overflow-y-auto" onClick={() => setShowActivity(false)}>
          <div className="bg-[#F5EFE7] w-full max-w-5xl border border-[#451C25]/20 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#451C25]/10 sticky top-0 bg-[#F5EFE7]">
              <div className="flex items-center gap-3">
                <Activity size={16} className="text-[#451C25]" />
                <h2 className="font-serif text-xl">ChatGPT connector activity</h2>
                <span className="text-[11px] tracking-[0.18em] uppercase text-[#6B4954]">{activity.length} entries</span>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="cm-activity-refresh" onClick={loadActivity} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase text-[#6B4954] hover:text-[#451C25]"><RefreshCw size={13} /> Refresh</button>
                <button data-testid="cm-activity-close" onClick={() => setShowActivity(false)} className="text-[#6B4954] hover:text-[#451C25]"><X size={18} /></button>
              </div>
            </div>
            <div className="p-6">
              <p className="text-[#6B4954] text-xs mb-4">Draft-only writes performed through the ChatGPT MCP connector. Published content and SEO pages cannot be changed by the connector.</p>
              {activityBusy ? (
                <div className="py-10 text-center text-[#6B4954] text-sm">Loading…</div>
              ) : activity.length === 0 ? (
                <div data-testid="cm-activity-empty" className="py-10 text-center text-[#6B4954] text-sm">No connector activity yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="cm-activity-table">
                    <thead>
                      <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[#6B4954] border-b border-[#451C25]/10">
                        <th className="py-2 pr-3">When</th><th className="py-2 pr-3">Action</th><th className="py-2 pr-3">Slug</th><th className="py-2 pr-3">Fields</th><th className="py-2 pr-3">Revision</th><th className="py-2 pr-3">Env</th><th className="py-2 pr-3">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activity.map((a) => (
                        <tr key={a.id} data-testid={`cm-activity-row-${a.id}`} className="border-b border-[#451C25]/5 hover:bg-[#EDE4D6]/40 align-top">
                          <td className="py-2 pr-3 whitespace-nowrap text-[#6B4954]">{fmtDate(a.timestamp)}</td>
                          <td className="py-2 pr-3 whitespace-nowrap">{a.action || a.tool}</td>
                          <td className="py-2 pr-3 font-mono text-[12px]">{a.slug || "—"}</td>
                          <td className="py-2 pr-3 text-[12px] text-[#6B4954]">{(a.fields_changed || []).join(", ") || "—"}</td>
                          <td className="py-2 pr-3 font-mono text-[11px] text-[#6B4954]">{a.revision_id ? String(a.revision_id).slice(0, 8) : "—"}</td>
                          <td className="py-2 pr-3 text-[11px] uppercase text-[#6B4954]">{a.environment || "—"}</td>
                          <td className="py-2 pr-3">
                            {a.success
                              ? <span className="px-2 py-0.5 text-[10px] tracking-wider uppercase bg-[#0D231B]/15 text-[#0D231B]">OK</span>
                              : <span title={a.error || ""} className="px-2 py-0.5 text-[10px] tracking-wider uppercase bg-[#7a1d1d]/15 text-[#7a1d1d]">{a.error ? String(a.error).split(":")[0] : "FAIL"}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Seg({ label, value, on, options, testid }) {
  return (
    <div data-testid={testid} className="inline-flex items-center border border-[#451C25]/20">
      {options.map(([v, l]) => (
        <button key={v} onClick={() => on(v)} className={`px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase ${value === v ? "bg-[#451C25] text-white" : "text-[#451C25] hover:bg-[#EDE4D6]"}`}>{l}</button>
      ))}
    </div>
  );
}
function DD({ label, value, on, options, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => on(e.target.value)} className="border border-[#451C25]/20 bg-white px-3 py-2 text-[11px] tracking-[0.12em] uppercase text-[#451C25] focus:outline-none">
      {options.map((o) => <option key={o} value={o}>{o === "all" ? `${label}: All` : o}</option>)}
    </select>
  );
}
